import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateSegmentPackageDemand } from '../../lib/calculations';
import { SEGMENTS } from '../../data/conjointData';
import { formatPercent, formatPrice } from '../../lib/formatters';
import { InfoButton } from '../common/Tooltip';

export function SegmentPackageMatrix() {
  const packages = useSimulatorStore(s => s.packages);
  const featureScaling = useSimulatorStore(s => s.assumptions.featureScaling);

  const matrix = useMemo(() =>
    packages.map(pkg => calculateSegmentPackageDemand(pkg.price, pkg.enabledFeatures, featureScaling)),
    [packages, featureScaling]
  );

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Per-Package Segment Demand</h3>
        <InfoButton helpKey="segmentPackageMatrix" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">How each configured package performs within each market segment (scenario).</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-secondary font-medium">Segment</th>
              {packages.map((pkg, i) => (
                <th key={i} className="text-right py-2 px-3 text-primary font-medium">
                  <div>{pkg.name}</div>
                  <div className="text-micro text-muted font-normal">{formatPrice(pkg.price)} / {pkg.enabledFeatures.length} feat.</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SEGMENTS.map((seg, si) => (
              <tr key={seg.name} className="border-b border-border/50 transition-colors"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                    <div>
                      <div className="text-primary text-sm">{seg.name}</div>
                      <div className="text-micro text-muted"><span className="font-mono">{(seg.percentage * 100).toFixed(1)}%</span> of market</div>
                    </div>
                  </div>
                </td>
                {matrix.map((pkgSegs, pi) => {
                  const rate = pkgSegs[si].segmentRate;
                  return (
                    <td key={pi} className="text-right py-2.5 px-3">
                      <span className="font-mono font-semibold" style={{ color: rate > 0.01 ? seg.color : 'var(--color-text-muted)' }}>
                        {formatPercent(rate)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t border-border">
              <td className="py-2.5 px-3 text-secondary font-medium">Overall (blended)</td>
              {matrix.map((pkgSegs, pi) => {
                const blended = pkgSegs.reduce((sum, s) => sum + s.weight * s.segmentRate, 0);
                return (
                  <td key={pi} className="text-right py-2.5 px-3 font-mono font-bold text-primary">
                    {formatPercent(blended)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-micro text-muted mt-3 italic">
        Feature lifts applied uniformly across segments. Segment-specific feature interactions were not measured in this study.
      </p>
    </div>
  );
}
